'use client';

import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@commenda/kakashi';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentVisibility } from '@commenda/types';
import {
  ConditionGroup,
  generateGroupId,
  groupsToVisibility,
  parseVisibilityToGroups,
  SingleCondition,
} from './conditions';

const VISIBILITY_OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'notEquals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'greaterThan', label: 'Greater Than' },
  { value: 'lessThan', label: 'Less Than' },
];

function ConditionRow({
  condition,
  availableFields,
  onUpdate,
}: {
  condition: SingleCondition;
  availableFields: { fieldId: string; label: string }[];
  onUpdate: (updates: Partial<SingleCondition>) => void;
}) {
  return (
    <div className="p-3 rounded-lg border border-border-tertiary bg-bg-primary space-y-3">
      <div className="flex items-center justify-between">
        <div className="space-y-2 flex-1">
          <Label>When field</Label>
          <Select
            value={condition.field ?? ''}
            onValueChange={(v) => onUpdate({ field: v })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a field" />
            </SelectTrigger>
            <SelectContent>
              {availableFields.map((f) => (
                <SelectItem key={f.fieldId} value={f.fieldId}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="space-y-2">
          <Label>Operator</Label>
          <Select
            value={condition.operator ?? 'equals'}
            onValueChange={(v) => onUpdate({ operator: v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VISIBILITY_OPERATORS.map((op) => (
                <SelectItem key={op.value} value={op.value}>
                  {op.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Value</Label>
          <Input
            value={String(condition.value ?? '')}
            onChange={(e) => {
              onUpdate({ value: e.target.value });
            }}
            placeholder="Enter value"
          />
        </div>
      </div>
    </div>
  );
}

function ConditionGroupEditor({
  group,
  groupIndex,
  availableFields,
  onUpdate,
  onRemove,
  showRemove,
}: {
  group: ConditionGroup;
  groupIndex: number;
  availableFields: { fieldId: string; label: string }[];
  onUpdate: (updates: Partial<ConditionGroup>) => void;
  onRemove: () => void;
  showRemove: boolean;
}) {
  const updateCondition = (
    condIndex: number,
    updates: Partial<SingleCondition>
  ) => {
    const newConditions = [...group.conditions];
    newConditions[condIndex] = { ...newConditions[condIndex], ...updates };
    onUpdate({ conditions: newConditions });
  };

  const addCondition = () => {
    onUpdate({
      conditions: [
        ...group.conditions,
        {
          field: availableFields[0]?.fieldId ?? '',
          operator: 'equals',
          value: '',
        },
      ],
    });
  };

  return (
    <div className="p-4 rounded-lg border border-border-secondary bg-bg-secondary space-y-3">
      <div className="flex items-center justify-between">
        <span className="txt-small-medium text-text-secondary">
          Group {groupIndex + 1}
        </span>
        {showRemove && (
          <Button type="button" variant="tertiary" size="sm" onClick={onRemove}>
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
      </div>

      {group.conditions.length > 1 && (
        <div className="space-y-2">
          <Label>Combine conditions within group with</Label>
          <Select
            value={group.operator}
            onValueChange={(v) => onUpdate({ operator: v as 'and' | 'or' })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="and">AND (all must match)</SelectItem>
              <SelectItem value="or">OR (any can match)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2">
        {group.conditions.map((condition, condIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={`${group.id}-${groupIndex}-cond-${condIndex}`}>
            {condIndex > 0 && (
              <div className="flex items-center justify-center py-1">
                <span className="txt-xs-medium text-text-tertiary px-2 py-0.5 rounded bg-bg-tertiary">
                  {group.operator.toUpperCase()}
                </span>
              </div>
            )}
            <ConditionRow
              condition={condition}
              availableFields={availableFields}
              onUpdate={(updates) => updateCondition(condIndex, updates)}
            />
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="tertiary"
        size="sm"
        onClick={addCondition}
        className="w-full"
      >
        <Plus className="w-3 h-3 mr-1" />
        Add Condition to Group
      </Button>
    </div>
  );
}

export function VisibilityEditor({
  visibility,
  availableFields,
  onChange,
}: {
  visibility?: ComponentVisibility;
  availableFields: { fieldId: string; label: string }[];
  onChange: (visibility: ComponentVisibility | undefined) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(!!visibility);

  const { topLevelOperator: initialOperator, groups: initialGroups } = useMemo(
    () => parseVisibilityToGroups(visibility),
    [visibility]
  );

  const [topLevelOperator, setTopLevelOperator] = useState<'and' | 'or'>(
    initialOperator
  );
  const [groups, setGroups] = useState<ConditionGroup[]>(initialGroups);

  const isInternalUpdate = useRef(false);

  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    const { topLevelOperator: newOp, groups: newGroups } =
      parseVisibilityToGroups(visibility);
    setTopLevelOperator(newOp);
    setGroups(newGroups);
  }, [visibility]);

  const hasVisibility = groups.length > 0;

  const applyChanges = useCallback(
    (newOperator: 'and' | 'or', newGroups: ConditionGroup[]) => {
      isInternalUpdate.current = true;
      const newVisibility = groupsToVisibility(newOperator, newGroups);
      onChange(newVisibility);
    },
    [onChange]
  );

  const handleEnableVisibility = (enabled: boolean) => {
    if (enabled) {
      const newGroups = [
        {
          id: generateGroupId(),
          operator: 'and' as const,
          conditions: [
            {
              field: availableFields[0]?.fieldId ?? '',
              operator: 'equals',
              value: '',
            },
          ],
        },
      ];
      setGroups(newGroups);
      setTopLevelOperator('and');
      applyChanges('and', newGroups);
    } else {
      setGroups([]);
      isInternalUpdate.current = true;
      onChange(undefined);
    }
    setIsExpanded(enabled);
  };

  const updateGroup = (
    groupIndex: number,
    updates: Partial<ConditionGroup>
  ) => {
    const newGroups = [...groups];
    newGroups[groupIndex] = { ...newGroups[groupIndex], ...updates };

    if (updates.conditions && updates.conditions.length === 0) {
      newGroups.splice(groupIndex, 1);
    }

    setGroups(newGroups);
    applyChanges(topLevelOperator, newGroups);
  };

  const addGroup = () => {
    const newGroups = [
      ...groups,
      {
        id: generateGroupId(),
        operator: 'and' as const,
        conditions: [
          {
            field: availableFields[0]?.fieldId ?? '',
            operator: 'equals',
            value: '',
          },
        ],
      },
    ];
    setGroups(newGroups);
    applyChanges(topLevelOperator, newGroups);
  };

  const removeGroup = (groupIndex: number) => {
    const newGroups = groups.filter((_, i) => i !== groupIndex);
    setGroups(newGroups);
    if (newGroups.length === 0) {
      isInternalUpdate.current = true;
      onChange(undefined);
    } else {
      applyChanges(topLevelOperator, newGroups);
    }
  };

  const handleTopLevelOperatorChange = (newOperator: 'and' | 'or') => {
    setTopLevelOperator(newOperator);
    applyChanges(newOperator, groups);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2"
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-text-tertiary" />
          ) : (
            <ChevronRight className="w-4 h-4 text-text-tertiary" />
          )}
          <h3 className="txt-small-medium text-text-secondary">
            Visibility Conditions
          </h3>
        </button>
        <Switch
          checked={hasVisibility}
          onCheckedChange={handleEnableVisibility}
        />
      </div>

      {isExpanded && hasVisibility && (
        <div className="space-y-3 pl-6 border-l-2 border-border-secondary">
          <p className="txt-xs-regular text-text-tertiary">
            Show this field only when conditions are met
          </p>

          {groups.length > 1 && (
            <div className="space-y-2">
              <Label>Combine groups with</Label>
              <Select
                value={topLevelOperator}
                onValueChange={(v) =>
                  handleTopLevelOperatorChange(v as 'and' | 'or')
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="and">
                    AND (all groups must match)
                  </SelectItem>
                  <SelectItem value="or">OR (any group can match)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {groups.map((group, groupIndex) => (
            <div key={group.id}>
              {groupIndex > 0 && (
                <div className="flex items-center justify-center py-2">
                  <span className="txt-xs-medium text-text-tertiary px-3 py-1 rounded-full bg-bg-tertiary border border-border-secondary">
                    {topLevelOperator.toUpperCase()}
                  </span>
                </div>
              )}
              <ConditionGroupEditor
                group={group}
                groupIndex={groupIndex}
                availableFields={availableFields}
                onUpdate={(updates) => updateGroup(groupIndex, updates)}
                onRemove={() => removeGroup(groupIndex)}
                showRemove={groups.length > 1}
              />
            </div>
          ))}

          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={addGroup}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Condition Group
          </Button>
        </div>
      )}

      {isExpanded && !hasVisibility && availableFields.length === 0 && (
        <p className="txt-xs-regular text-text-tertiary pl-6">
          Add other fields to the form to create visibility conditions
        </p>
      )}
    </div>
  );
}

